import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { useProgramPanel } from 'contexts/programPanelContext';
import { Loader } from 'components/_new/Loader';
import { ErrorContainer } from 'components/_new/ErrorContainer/ErrorContainer';
import { Button } from 'components/_new/Button';
import { timelineApi } from 'api/timeline';
import { Box, BoxSection } from 'components/_new/Box';
import { TimelineEventItem, TimelineEventItemTypeEnum, TimelineParticipationItem } from 'types/Timeline';
import { dateFormat } from 'utils/dateFormat';
import { Icon } from 'components/_new/Icon';
import { MandatoryTag, RequireConfirmationTag } from 'components/_common/timeline/misc/TimelineComponents';
import { classNames } from 'primereact/utils';
import { Divider } from 'components/_new/Divider';
import { ApplicationRoleEnum } from 'types/Application';
import { useGlobal } from 'contexts/globalContext';
import { Dialog, DialogBaseExtendProps, DialogConfirm } from 'components/_new/Dialog';
import { Table } from 'components/_new/Table/Table';
import { Tooltip } from 'components/_new/Tooltip/Tooltip';
import { Avatar } from 'components/_new/Avatar';
import { userDisplayName } from 'utils/userDisplayName';
import { parseHtml } from 'utils/htmlParser';

type ProgramTimelineParticipationsDialogProps = DialogBaseExtendProps & {
	eventId?: number;
};
const ProgramTimelineParticipationsDialog = ({ eventId, ...dialogProps }: ProgramTimelineParticipationsDialogProps) => {
	const { t } = useTranslation();
	const { data: programData } = useProgramPanel();

	const { data, isFetched, isSuccess } = useQuery(
		['TIMELINE_EVENT_PARTICIPATIONS', { programId: Number(programData?.id), eventId }],
		() => timelineApi.program.getTimelineEventsParticipations(Number(programData?.id), Number(eventId)),
		{
			enabled: dialogProps.visible && Boolean(eventId),
		}
	);

	const visible = dialogProps.visible && isFetched;

	return (
		<Dialog
			title={t('timeline.programPanel.details.participation.confirmationList')}
			{...dialogProps}
			visible={visible}
		>
			{data?.length === 0 && <p>{t('misc.empty')}</p>}
			{isSuccess && (
				<Table<TimelineParticipationItem>
					data={data}
					keyOptionProp="key"
					columns={[
						{
							label: t('programPanel.application.applicationRole'),
							name: 'role',
							body: ({ application }) => t(`misc.${application.applicationRole}`),
						},
						{
							label: t('programPanel.application.name'),
							name: 'name',
							body: ({ application }) => {
								return (
									<>
										<Tooltip target={`#application-${application.id}`} position="bottom" />
										<Link
											id={`application-${application.id}`}
											to={`/program/${programData?.name}/application/${application.id}`}
											className="w-full flex flex-row gap-2 flex-nowrap justify-items-start align-items-center"
											data-pr-tooltip={
												application.deletedAt
													? t('programPanel.application.willBeDeleted', {
															date: dateFormat(application.deletedAt, 'date'),
													  })
													: undefined
											}
										>
											<Avatar
												src={application.programMembership.user.avatar}
												size="md"
												name={userDisplayName(application.programMembership.user, null)}
											/>
											<span className={classNames({ 'text-error': application.deletedAt })}>
												{userDisplayName(application.programMembership.user)}
											</span>
										</Link>
									</>
								);
							},
						},
						{
							label: t('timeline.programPanel.details.participation.confirmedAt'),
							name: 'confirmedAt',
							body: ({ confirmedAt }) => dateFormat(confirmedAt, 'full'),
						},
					]}
				/>
			)}
		</Dialog>
	);
};

type ProgramTimelineEventDetailsInsideProps = {
	timelineEvent: TimelineEventItem;
};

export const ProgramTimelineEventDetailsInside = ({ timelineEvent }: ProgramTimelineEventDetailsInsideProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { toastRef } = useGlobal();
	const { panelPath, data: programData, context } = useProgramPanel();

	const {
		eventId,
		summary,
		intro,
		description,
		type,
		forApplicationRole,
		dayDate,
		startAt,
		endAt,
		mandatory,
		requireConfirmation,
		onlineMeetingUrl,
		visits,
		participations,
		participantLimit,
	} = timelineEvent;

	const flagcolor = useMemo(() => {
		switch (type) {
			case TimelineEventItemTypeEnum.EVENT:
				return '#f674cb';
			case TimelineEventItemTypeEnum.MILESTONE:
				return '#435cd8';
			case TimelineEventItemTypeEnum.SESSION:
				return 'gray';
			default:
				return 'gray';
		}
	}, [type]);

	const menteeLocalMeetingUrl = `${process.env.REACT_APP_FRONT_URL}/panel/${programData?.name}/mentee/timeline#event-${eventId}`;
	const mentorLocalMeetingUrl = `${process.env.REACT_APP_FRONT_URL}/panel/${programData?.name}/mentor/timeline#event-${eventId}`;

	const [participationDialogOpen, setParticipationDialogOpen] = useState(false);

	const [removeConfirmDialog, setRemoveConfirmDialog] = useState(false);
	const { mutate } = useMutation(
		() => timelineApi.program.removeTImelineEvent(Number(programData?.id), Number(eventId)),
		{
			onSuccess: () => {
				setRemoveConfirmDialog(false);
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('timeline.programPanel.removedSuccessfully'),
				});
				navigate({ pathname: `/${panelPath}/timeline/list` });
			},
		}
	);

	const handleNotificationClick = () => {
		navigate({ pathname: `/${panelPath}/communication/add`, search: `?timelineEvent=${timelineEvent.eventId}` });
	};

	return (
		<>
			<DialogConfirm
				visible={removeConfirmDialog}
				onHide={() => {
					setRemoveConfirmDialog(false);
				}}
				onConfirm={mutate}
			/>
			<ProgramTimelineParticipationsDialog
				eventId={eventId || undefined}
				visible={participationDialogOpen}
				onHide={() => {
					setParticipationDialogOpen(false);
				}}
			/>
			<div className="flex flex-column gap-4">
				<div className="flex flex-column gap-4 sm:flex-row">
					<Box variant="white-bordered" className="flex-1">
						<BoxSection>
							{mandatory && requireConfirmation && (
								<div className="flex flex-row gap-1 justify-content-start">
									{mandatory && <MandatoryTag />}
									{requireConfirmation && <RequireConfirmationTag />}
								</div>
							)}

							<div className="flex flex-column gap-0 text-lg strong">
								<span>{summary}</span>
								{/* {data.subtitle && <span className=" text-primary">{data.subtitle}</span>} */}
							</div>

							<div className="flex flex-row w-full gap-4 justify-content-between">
								<div className="flex flex-row gap-3 flex-nowrap bg-new-gray rounded-md px-2 py-1">
									<div className="flex flex-row gap-1 justify-content-center align-items-center">
										<Icon name="calendar" iconStyle="regular" className="text-purple" />
										<span className="text-nowrap">
											{dateFormat(startAt || dayDate, 'dayAndMonth')}
										</span>
									</div>
									{startAt && type !== TimelineEventItemTypeEnum.MILESTONE && (
										<div className="flex flex-row gap-1 justify-content-center align-items-center">
											<Icon name="clock" iconStyle="regular" className="text-purple" />
											<span>{`${dateFormat(startAt, 'hours')}`}</span>
											{endAt && <span>{` - ${dateFormat(endAt, 'hours')}`}</span>}
										</div>
									)}
								</div>
							</div>

							{(intro || description) && (
								<>
									<Divider />
									{intro && intro.length > 0 && <div>{intro}</div>}
									{description && description.length > 0 && (
										<div className="bg-new-gray p-3 rounded-md user-content">
											{parseHtml(description || '', { nl2br: false })}
										</div>
									)}
									<Divider />
								</>
							)}

							<div>
								<h3 className="py-0 my-0">{t('timeline.item.onlineMeetingUrl')}:</h3>
								{onlineMeetingUrl ? (
									<>
										<p className="bg-gray-200 text-gray-700 text-wrap-any border-round p-2 text-xs selectable">
											{onlineMeetingUrl}
										</p>
										<Button
											label={t('misc.copyToClipboard')}
											variant="primary-text"
											variantSize="sm"
											block
											icon="copy"
											onClick={() => {
												navigator.clipboard.writeText(onlineMeetingUrl);
												toastRef?.current?.show({
													severity: 'success',
													life: 3000,
													summary: t('misc.success'),
													detail: t('misc.copiedToClipboard'),
												});
											}}
										/>
									</>
								) : (
									<p>{t('misc.unavailable')}</p>
								)}
							</div>

							{context === 'program-admin' && (
								<div className="flex flex-row justify-content-center">
									<Button
										label={t('timeline.programPanel.prepareEventNotification')}
										onClick={handleNotificationClick}
									/>
								</div>
							)}
						</BoxSection>
					</Box>

					<div className="flex flex-column gap-2 w-300-auto">
						<Box variant="white-bordered">
							<BoxSection header={t('timeline.programPanel.details.views.title')}>
								<div className="flex flex-row gap-2 justify-content-between">
									<div>
										<Icon name="eye" className="text-purple" />
										<span className="ml-2">{t('misc.manyMentees')}</span>
									</div>
									<span className="font-bold">{visits?.mentee || 0}</span>
								</div>
								<div className="flex flex-row gap-2 justify-content-between">
									<div>
										<Icon name="eye" className="text-purple" />
										<span className="ml-2">{t('misc.manyMentors')}</span>
									</div>
									<span className="font-bold">{visits?.mentor || 0}</span>
								</div>
							</BoxSection>
						</Box>

						{requireConfirmation && (
							<Box variant="white-bordered">
								<BoxSection header={t('timeline.programPanel.details.participation.title')}>
									{participantLimit && participantLimit > 0 && (
										<div className="flex flex-row gap-2 justify-content-between">
											<span>Limit: </span>
											<span className="font-bold">
												{participations?.all || 0}/{participantLimit}
											</span>
										</div>
									)}
									<div className="flex flex-row gap-2 justify-content-between">
										<div>
											<Icon name="user-plus" className="text-purple" />
											<span className="ml-2">{t('misc.manyMentees')}</span>
										</div>
										<span className="font-bold">{participations?.mentee || 0}</span>
									</div>
									<div className="flex flex-row gap-2 justify-content-between">
										<div>
											<Icon name="user-plus" className="text-purple" />
											<span className="ml-2">{t('misc.manyMentors')}</span>
										</div>
										<span className="font-bold">{participations?.mentor || 0}</span>
									</div>

									<Button
										label={t('timeline.programPanel.details.participation.show')}
										className="mx-auto"
										onClick={() => {
											setParticipationDialogOpen(true);
										}}
										disabled={!participations.all}
										block
									/>
								</BoxSection>
							</Box>
						)}

						<Box variant="white-bordered">
							<BoxSection header={t('timeline.programPanel.details.links.title')}>
								<div>
									<h3>{t('programPanel.knowledge.forMentees')}:</h3>
									{!forApplicationRole || forApplicationRole === ApplicationRoleEnum.MENTEE ? (
										<>
											<p className="bg-gray-200 text-gray-700 text-wrap-any border-round p-2 text-xs selectable">
												{menteeLocalMeetingUrl}
											</p>
											<Button
												label={t('misc.copyToClipboard')}
												variant="primary-text"
												variantSize="sm"
												block
												icon="copy"
												onClick={() => {
													navigator.clipboard.writeText(menteeLocalMeetingUrl);
													toastRef?.current?.show({
														severity: 'success',
														life: 3000,
														summary: t('misc.success'),
														detail: t('misc.copiedToClipboard'),
													});
												}}
											/>
										</>
									) : (
										<p>{t('misc.unavailable')}</p>
									)}
								</div>
								<div>
									<h3>{t('programPanel.knowledge.forMentors')}:</h3>
									{!forApplicationRole || forApplicationRole === ApplicationRoleEnum.MENTOR ? (
										<>
											<p className="bg-gray-200 text-gray-700 text-wrap-any border-round p-2 text-xs selectable">
												{mentorLocalMeetingUrl}
											</p>
											<Button
												label={t('misc.copyToClipboard')}
												variant="primary-text"
												variantSize="sm"
												block
												icon="copy"
												onClick={() => {
													navigator.clipboard.writeText(mentorLocalMeetingUrl);
													toastRef?.current?.show({
														severity: 'success',
														life: 3000,
														summary: t('misc.success'),
														detail: t('misc.copiedToClipboard'),
													});
												}}
											/>
										</>
									) : (
										<p>{t('misc.unavailable')}</p>
									)}
								</div>
							</BoxSection>
						</Box>

						<div className="flex flex-column gap-2">
							<Button
								label={t('actions.edit')}
								className="w-full"
								onClick={() => {
									navigate(`/${panelPath}/timeline/edit/${eventId}`);
								}}
							/>

							<Button
								label={t('actions.remove')}
								className="w-full"
								variant="danger"
								onClick={() => {
									setRemoveConfirmDialog(true);
								}}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export const ProgramTimelineEventDetails = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { data: program, panelPath } = useProgramPanel();
	const params = useParams();
	const eventId = Number(params?.eventId);

	const hasEvent = Boolean(eventId) && !Number.isNaN(eventId);
	const {
		data: timelineEvent,
		isLoading: timelineEventLoading,
		isSuccess: timelineEventSuccess,
		isError: timelineEventIsError,
		error: timelineEventError,
		refetch: timelineEventRefetch,
	} = useQuery(
		['TIMELINE_EVENT', { programId: Number(program?.id), eventId }],
		() => timelineApi.program.getTimelineEventForEdit(Number(program?.id), eventId),
		{
			enabled: hasEvent,
		}
	);

	if (!hasEvent) {
		return <Navigate to={{ pathname: `/${panelPath}/timeline/list` }} />;
	}

	if (hasEvent && timelineEventIsError) {
		return <ErrorContainer action={timelineEventRefetch} error={timelineEventError} />;
	}

	if (hasEvent && timelineEventLoading) {
		return <Loader />;
	}

	if (hasEvent && timelineEventSuccess) {
		return (
			<>
				<Helmet title={t('timeline.programPanel.detailsTitle')} />
				<SubPageTitle
					title={t('timeline.programPanel.detailsTitle')}
					additional={program?.displayName}
					right={() => (
						<Button
							icon="arrow-left"
							variant="primary-text"
							variantSize="sm"
							onClick={() => {
								navigate(`/${panelPath}/timeline/list`);
							}}
							label={t('actions.back')}
						/>
					)}
				/>

				<ProgramTimelineEventDetailsInside timelineEvent={timelineEvent} />
			</>
		);
	}

	return null;
};
